import type { AmplitudeDetailsType } from '@readme/api/src/core/amplitude';

import * as amplitude from '@amplitude/analytics-browser';

/**
 * Returns details about the client's browser environment that is formatted for
 * our custom `x-amplitude-details` header used for Amplitude tracking.
 * @link packages/api/src/core/amplitude.ts
 * @example
 * ```ts
 * const amplitudeDetails = getAmplitudeDetails();
 *
 * fetch('/api/data', {
 *   headers: {
 *     'x-amplitude-details': JSON.stringify(amplitudeDetails),
 *   },
 * });
 * ```
 */
export default function getAmplitudeDetails(): AmplitudeDetailsType {
  const userAgent = navigator.userAgent;
  let deviceFamily = 'Unknown';
  let os = 'Unknown';

  // Detect device family (Amplitude format)
  if (/Android/i.test(userAgent)) {
    deviceFamily = 'Android';
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    deviceFamily = 'iOS';
  } else if (/Windows/i.test(userAgent)) {
    deviceFamily = 'Windows';
  } else if (/Mac/i.test(userAgent)) {
    deviceFamily = 'Mac OS X';
  } else if (/Linux/i.test(userAgent)) {
    deviceFamily = 'Linux';
  }

  // Detect OS (browser name and version)
  if (/Chrome/i.test(userAgent)) {
    const match = userAgent.match(/Chrome\/(\d+)/);
    const version = match ? match[1] : '';
    os = `Chrome ${version}`;
  } else if (/Firefox/i.test(userAgent)) {
    const match = userAgent.match(/Firefox\/(\d+)/);
    const version = match ? match[1] : '';
    os = `Firefox ${version}`;
  } else if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) {
    const match = userAgent.match(/Version\/(\d+)/);
    const version = match ? match[1] : '';
    os = `Safari ${version}`;
  } else if (/Edge/i.test(userAgent)) {
    const match = userAgent.match(/Edge\/(\d+)/);
    const version = match ? match[1] : '';
    os = `Edge ${version}`;
  }

  const payload = {
    sessionId: amplitude.getSessionId() || undefined,
    deviceId: amplitude.getDeviceId() || undefined,
    deviceFamily,
    os,
  };

  return payload;
}
